import { Check, Close, Download, Refresh, Send } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import FlexBetween from 'components/flexbox/FlexBetween';
import AuthContext from 'contexts/JWTAuth';
import useBoolean from 'hooks/useBoolean';
import ChevronLeft from 'icons/ChevronLeft';
import FileUpload from 'page-sections/group-registration-detail/FileUpload';
import ListHeader from 'page-sections/group-registration-detail/ListHeader';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import downloadFile from 'utils/downloadFile';
import formatCurrency from 'utils/formatCurrency';
import AlertInfo from './AlertInfo';

const StyledFlexBox = styled(FlexBetween)(({ theme }) => ({
    flexWrap: 'wrap',
    [theme.breakpoints.down(500)]: {
        width: '100%',
        '& .MuiInputBase-root': {
            maxWidth: '100%',
        },
        '& .MuiButton-root': {
            width: '100%',
            marginTop: 15,
        },
    },
}));

const GroupRegistrationDataPage = () => {
    const navigate = useNavigate();
    const [error, setError] = useState();
    const [file, setFile] = useState(false);
    const id = useParams('id');
    const [data, setData] = useState([]);
    const [fileName, setFileName] = useState();

    const loading = useBoolean();
    const loadingApprove = useBoolean();
    const loadingReject = useBoolean();

    const { user } = useContext(AuthContext);

    const handleSubmit = async (status) => {
        loading.onTrue();

        if (status === 'rejected') {
            loadingReject.onTrue();
        } else if (status === 'gl_approved' || status === 'invoiced') {
            loadingApprove.onTrue();
        }

        await axiosInstance
            .patch(`/api/register-groups/${id['id']}`, { status })
            .then(() => {
                if (user?.role === 'group') {
                    window.location.reload();
                } else {
                    navigate('/dashboard/operator/register-group-list');
                }
            })
            .catch((err) => console.log(err))
            .finally(() => {
                loading.onFalse();
                loadingApprove.onFalse();
                loadingReject.onFalse();
            });
    };

    const navigateDetail = (role, id) => {
        if (role === 'group') {
            navigate(`/dashboard/group-registration-detail/${id}`);
        } else {
            navigate(`/dashboard/group-registration-detail-operator/${id}`);
        }
    };

    const handleRemoveFile = () => {
        setFileName();
    };

    const handleDropFile = useCallback(
        async (acceptedFiles, fileRejections) => {
            setError();

            fileRejections.forEach((file) => {
                file.errors.forEach((err) => {
                    if (err.code === 'file-too-large') {
                        setError(`Error: ${err.message}`);
                    }

                    if (err.code === 'file-invalid-type') {
                        setError(`Error: ${err.message}`);
                    }
                });
            });

            if (acceptedFiles.length > 0) {
                setFile(true);
                setFileName(acceptedFiles[0].name);
                const formData = new FormData();
                formData.append('id', id['id']);
                formData.append('file', acceptedFiles[0]);
                formData.append('glFileName', acceptedFiles[0].name);
                formData.append('status', 'gl_uploaded');

                await axiosInstance
                    .post('/api/register-groups/uploadGL', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    })
                    .then(() => navigate(`/dashboard/group-registration`))
                    .catch((err) => console.log(err))
                    .finally(() => {
                        setFile(false);
                    });
            }
        },
        [id, navigate],
    );

    const handleDownload = async (type) => {
        loading.onTrue();

        if (type === 'template_gl') {
            const url = `${process.env.REACT_APP_BASE_URL}/assets/templates/FORMAT GL.docx`;
            await downloadFile(url);

            loading.onFalse();

            return;
        }

        let filename;

        switch (type) {
            case 'excel':
                filename = data.fileName;
                break;
            case 'invoice':
                filename = `invoice-${data.invoiceNumber}.pdf`;
                break;
            default:
                filename = data.glFileName;
                break;
        }

        const url = `/api/register-groups/download/${id['id']}/${filename}`;
        await downloadFile(url);

        loading.onFalse();
    };

    useEffect(() => {
        const getData = async () => {
            await axiosInstance
                .get(`/api/register-groups/${id['id']}`)
                .then(({ data }) => {
                    setData(data);
                })
                .catch((err) => console.log(err));
        };

        getData();
    }, [id]);

    const regenerateInvoice = async () => {
        await axiosInstance
            .post('/api/register-groups/regenerate-invoice', { id: id['id'] })
            .then(({ data }) => console.log(data))
            .catch((err) => console.log(err));
    };
    return (
        <Box pt={2} pb={4}>
            <Button
                disableRipple
                startIcon={<ChevronLeft />}
                onClick={() => navigate(-1)}
            >
                {' '}
                Back{' '}
            </Button>
            <ListHeader />

            <AlertInfo status={data.status?.value} />

            <Card sx={{ padding: 2, mb: 2 }}>
                <StyledFlexBox>
                    <Box>
                        <Typography variant="body">
                            Invoice Number: {data?.invoiceNumber}
                        </Typography>{' '}
                        <br />
                        <Typography variant="body">
                            Status:{' '}
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: data?.status?.color || '#424242',
                                    backgroundColor:
                                        data?.status?.bgColor || '#eeeeee',
                                }}
                            >
                                {data.status?.label}
                            </Box>
                        </Typography>{' '}
                        <br />
                        <Typography variant="body">
                            Sponsor: {data.sponsor?.name}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                        {/* start condition untuk menampilkan button regenerate invoice dengan status invoiced */}
                        {data.actions?.canRegenerate &&
                            user?.role === 'admin' && (
                                <Button
                                    onClick={() => regenerateInvoice()}
                                    variant="contained"
                                >
                                    <Tooltip title="Regenerate Invoice">
                                        <Refresh />
                                    </Tooltip>
                                </Button>
                            )}
                        {/* end condition untuk menampilkan button regenerate invoice dengan status invoiced */}

                        {/* Start Button untuk download file excel */}
                        <Button
                            disabled={loading.value}
                            variant="contained"
                            color="primary"
                            onClick={() => handleDownload('excel')}
                            sx={{ cursor: 'pointer', fontSize: 14, mr: 2 }}
                            startIcon={<Download />}
                        >
                            Download Excel
                        </Button>
                        {/* End Button untuk download file excel */}

                        {/* start condition untuk menampilkan button download template GL */}
                        {data.actions?.canUploadGl &&
                            user?.role === 'group' && (
                                <>
                                    <Button
                                        disabled={loading.value}
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                            handleDownload('invoice')
                                        }
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            mr: 2,
                                        }}
                                        startIcon={<Download />}
                                    >
                                        Download Invoice
                                    </Button>
                                    <Button
                                        disabled={loading.value}
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                            handleDownload('template_gl')
                                        }
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            mr: 2,
                                        }}
                                        startIcon={<Download />}
                                    >
                                        Download Template GL
                                    </Button>
                                </>
                            )}
                        {/* end condition untuk menampilkan button download template GL */}

                        {data.glFileName && (
                            <Button
                                disabled={loading.value}
                                variant="contained"
                                color="primary"
                                onClick={() => handleDownload('gl')}
                                sx={{ cursor: 'pointer', fontSize: 14, mr: 2 }}
                                startIcon={<Download />}
                            >
                                Download GL
                            </Button>
                        )}

                        {/* start condition untuk menampilkan button submit oleh user grup */}
                        {data.actions?.canSubmit && user?.role === 'group' && (
                            <Button
                                variant="contained"
                                onClick={() => handleSubmit('submitted')}
                                color="primary"
                                sx={{ fontSize: 14 }}
                                endIcon={<Send />}
                                disabled={loading.value}
                            >
                                Submit Registration
                            </Button>
                        )}
                        {/* end condition untuk menampilkan button submit oleh user grup */}

                        {/* start condition untuk menampilkan button reject / approve oleh admin */}
                        {data.actions?.canApprove && user?.role === 'admin' && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 1,
                                }}
                            >
                                <Button
                                    disabled={loading.value}
                                    sx={{ mr: 3 }}
                                    size="small"
                                    color="error"
                                    variant="contained"
                                    onClick={() => handleSubmit('rejected')}
                                    startIcon={<Close />}
                                >
                                    {loadingReject.value ? (
                                        <>
                                            Processing . . .
                                            <CircularProgress size={20} />
                                        </>
                                    ) : (
                                        'Reject'
                                    )}
                                </Button>
                                <Button
                                    disabled={loading.value}
                                    size="small"
                                    startIcon={<Check />}
                                    variant="contained"
                                    onClick={() => handleSubmit('invoiced')}
                                >
                                    {loadingApprove.value ? (
                                        <>
                                            Processing . . .
                                            <CircularProgress size={20} />
                                        </>
                                    ) : (
                                        'Approve'
                                    )}
                                </Button>
                            </Box>
                        )}
                        {/* end condition untuk menampilkan button reject / approve oleh operator */}

                        {/* start condition untuk menampilkan button download GL, reject / approve oleh operator setelah user group upload GL */}
                        {data.actions?.canApproveGL &&
                            ['operator', 'admin'].includes(user?.role) && (
                                <Box>
                                    <Button
                                        disabled={loading.value}
                                        sx={{ mr: 2 }}
                                        size="small"
                                        color="error"
                                        variant="contained"
                                        onClick={() => handleSubmit('rejected')}
                                        startIcon={<Close />}
                                    >
                                        {loadingReject.value ? (
                                            <>
                                                Processing . . .
                                                <CircularProgress size={20} />
                                            </>
                                        ) : (
                                            'Reject GL'
                                        )}
                                    </Button>
                                    <Button
                                        disabled={loading.value}
                                        size="small"
                                        variant="contained"
                                        startIcon={<Check />}
                                        onClick={() =>
                                            handleSubmit('gl_approved')
                                        }
                                    >
                                        {loadingApprove.value ? (
                                            <>
                                                Processing . . .
                                                <CircularProgress size={20} />
                                            </>
                                        ) : (
                                            'Approve GL'
                                        )}
                                    </Button>
                                </Box>
                            )}
                        {/* end condition untuk menampilkan button download GL, reject / approve oleh operator setelah user group upload GL */}
                    </Box>
                </StyledFlexBox>
            </Card>

            <TableContainer component={Paper} sx={{ p: 3 }}>
                <Table
                    size="small"
                    aria-label="caption table"
                    sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}
                >
                    <TableBody>
                        <TableRow key={1}>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                                No
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                                Name
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                                E-mail
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                                Package
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 'bold' }}
                            >
                                Workshop
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{ fontWeight: 'bold' }}
                            >
                                Value
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 'bold' }}
                            >
                                Action
                            </TableCell>
                        </TableRow>

                        {data.details?.map((row, index) => (
                            <TableRow key={index}>
                                <TableCell align="center">
                                    {index + 1}
                                </TableCell>
                                <TableCell align="left">
                                    {row.fullName}
                                </TableCell>
                                <TableCell align="left">{row.email}</TableCell>
                                <TableCell align="left">
                                    {row?.package?.name}
                                </TableCell>
                                <TableCell align="center">
                                    {row.countWorkshop}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCurrency(row.invoiceAmount ?? 0)}
                                </TableCell>
                                <TableCell align="center">
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            navigateDetail(
                                                localStorage.getItem('role'),
                                                row.id,
                                            )
                                        }
                                    >
                                        Detail
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}

                        { data?.discount > 0 && (
                            <>
                                <TableRow>
                                    <TableCell align="right" colSpan={3}></TableCell>
                                    <TableCell
                                        colSpan={2}
                                        align="left"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        Total Amount
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {formatCurrency(
                                            data?.totalAmount ?? 0,
                                        )}
                                    </TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" colSpan={3}></TableCell>
                                    <TableCell
                                        colSpan={2}
                                        align="left"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        Discount
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {formatCurrency(
                                            data?.discount ?? 0,
                                        )}
                                    </TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </>
                        )}

                        <TableRow>
                            <TableCell align="right" colSpan={3}></TableCell>
                            <TableCell
                                colSpan={2}
                                align="left"
                                sx={{ fontWeight: 'bold' }}
                            >
                                Invoice Amount
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{ fontWeight: 'bold' }}
                            >
                                {formatCurrency(
                                    (data?.totalAmount ?? 0) - (data?.discount ?? 0),
                                )}
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* start condition untuk menampilkan section upload GL user grup setelah approve */}
            {data.actions?.canUploadGl && 'group' === user?.role && (
                <Card sx={{ mt: 4 }}>
                    <FileUpload
                        handleRemove={handleRemoveFile}
                        onDrop={handleDropFile}
                        error={error}
                        file={file}
                        fileName={fileName}
                    />
                </Card>
            )}
            {/* end condition untuk menampilkan section upload GL user grup setelah approve */}
        </Box>
    );
};

export default GroupRegistrationDataPage;
